import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { GmailAccount } from '../types/gmail';

export function useGmailAccounts() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<GmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    fetchAccounts();
  }, [user]);

  const fetchAccounts = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('nrm_gmail_credentials')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setAccounts(data || []);
    } catch (err) {
      setError('Failed to load Gmail accounts');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const disconnect = async (accountId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('nrm_gmail_credentials')
        .delete()
        .eq('id', accountId);

      if (deleteError) throw deleteError;
      await fetchAccounts();
    } catch (err) {
      setError('Failed to disconnect account');
      console.error('Disconnect error:', err);
    }
  };

  const refreshToken = async (accountId: string) => {
    setRefreshing(true);
    try {
      const account = accounts.find(a => a.id === accountId);
      if (!account) throw new Error('Account not found');

      const { data: tokens, error: refreshError } = await supabase.functions.invoke(
        'refresh-gmail-token',
        {
          body: { refreshToken: account.refresh_token }
        }
      );

      if (refreshError) throw refreshError;

      const { error: updateError } = await supabase
        .from('nrm_gmail_credentials')
        .update({
          access_token: tokens.access_token,
          token_expiry: new Date(Date.now() + tokens.expires_in * 1000).toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', accountId);

      if (updateError) throw updateError;
      await fetchAccounts();
    } catch (err) {
      console.error('Failed to refresh token:', err);
      throw err;
    } finally {
      setRefreshing(false);
    }
  };

  return { 
    accounts, 
    loading, 
    error, 
    disconnect, 
    refreshToken,
    refreshing,
    refresh: fetchAccounts 
  };
}